import { RefreshCw } from "lucide-react";
import PaymentAccount from "./PaymentAccount";
import ShippingSummaryValue from "./ShippingSummaryValue";
import FinancialBalanceStatus from "./FinancialBalanceStatus";
import { hasVerifiedPaymentTotal } from "./paymentReconciliation";
import { invoiceBadge } from "./invoiceStatus";
import { financialBalanceStatus } from "./financialStatusHelpers";
import { orderProgress, canOmitFutureBillCaveat, PROGRESS_STATUSES } from "./orderProgressPresentation";

export const rupiah = (n) =>
    "Rp" + Math.round(Math.abs(Number(n) || 0)).toLocaleString("id-ID");

function Stat({ label, value, tone = "ink", testId, className = "" }) {
    const tones = {
        ink: "text-[color:var(--wx-ink)]",
        green: "text-emerald-700",
        red: "text-red-600",
        blue: "text-[color:var(--wx-blue)]",
    };
    return (
        <div
            data-testid={testId}
            className={`rounded-xl border border-[color:var(--wx-line)] bg-[color:var(--wx-surface)] ${testId === "stat-payment" ? "px-2" : "px-3"} py-2.5 ${className}`}
        >
            <p className={`${testId === "stat-payment" ? "text-[8px] min-[360px]:text-[9px] tracking-normal whitespace-nowrap" : "text-[10px] uppercase tracking-wider"} text-[color:var(--wx-muted)] font-semibold leading-tight`}>
                {label}
            </p>
            <div className={`mt-1 text-sm font-bold leading-tight ${tones[tone]}`}>
                {value}
            </div>
        </div>
    );
}

export default function OrderSummaryCard({ data, onRefresh, refreshing, updatedAt, refreshDelayed = false }) {
    const s = data.summary || {};
    const po = data.po || {};
    const totalDapat = Number(s.total_dapat) || 0;
    const progress = orderProgress(data.orders, s);
    const balanceStatus = financialBalanceStatus(s, data.shipping_summary, po);
    const money = (value) => balanceStatus.state !== "unavailable" &&
        Number.isSafeInteger(value) && value >= 0 ? rupiah(value) : "—";
    const invoice = invoiceBadge(data);
    const verified = hasVerifiedPaymentTotal(data.payment_reconciliation, s);
    const balanceTone = { underpaid: "red", overpaid: "blue", paid: "green" }[balanceStatus.state] || "ink";
    const omitFutureBillCaveat = canOmitFutureBillCaveat(data, progress, verified ? balanceStatus.state : "unavailable");

    return (
        <section data-testid="order-summary" className="wx-card p-4">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="wx-eyebrow">Pesanan Anda</p>
                    <p
                        data-testid="customer-name"
                        className="mt-1 text-lg font-semibold text-[color:var(--wx-ink)] leading-tight truncate"
                    >
                        {data.customer?.name || "-"}
                    </p>
                    {(po.id || po.name) && (
                        <p data-testid="po-info" className="mt-0.5 text-[11px] text-[color:var(--wx-muted)]">
                            {po.name || po.id}
                        </p>
                    )}
                </div>
                <div className="flex shrink-0 items-start gap-2">
                    <div className="flex flex-col items-end gap-1" data-testid="order-status-badges">
                        {po.status === 'OPEN' || po.status === 'CLOSED' || po.status === 'CLOSE' ? <span className="wx-order-status">{po.status === 'OPEN' ? 'Open PO' : 'Closed PO'}</span> : null}
                        <span className={`wx-order-status wx-order-status--${invoice.tone}`}>{invoice.label}</span>
                    </div>
                <button
                    type="button"
                    data-testid="refresh-btn"
                    onClick={onRefresh}
                    disabled={refreshing}
                    aria-label="Muat ulang"
                    className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg border border-[color:var(--wx-line)] text-[color:var(--wx-muted)] hover:text-[color:var(--wx-ink)] disabled:opacity-50"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                </button>
                </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2">
                <Stat testId="stat-dapat" label="Total Barang Dapat" value={`${totalDapat} pcs`} tone="green" />
                <Stat testId="stat-belanja" label="Total Belanja Barang" value={money(s.total_belanja)} />
                <Stat testId="stat-ongkir" label="Ongkir" value={balanceStatus.state === "unavailable" ? "—" : <ShippingSummaryValue amount={s.ongkir} shipping={data.shipping_summary} />} />
                <Stat testId="stat-payment" label="Pembayaran terverifikasi" value={verified ? money(s.total_payment) : "—"} />
                {balanceStatus.state !== "unavailable" && Number.isSafeInteger(s.diskon) && s.diskon > 0 && (
                    <div data-testid="stat-diskon" className="col-span-2 flex items-center justify-between rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2.5 text-emerald-700">
                        <span className="text-[10px] font-semibold tracking-wider">DISKON</span>
                        <span className="text-sm font-bold">−{rupiah(s.diskon)}</span>
                    </div>
                )}
                <Stat testId="stat-sisa" label="Status pembayaran" value={<FinancialBalanceStatus summary={verified ? s : {...s, finance_state: "unavailable"}} shipping={data.shipping_summary} po={po} />} tone={balanceTone} className="col-span-2" />
            </div>

            <PaymentAccount key={po.id || "no-po"} data={data} pending={refreshing || refreshDelayed} />

            <p data-testid="summary-bill-note" className="mt-2 text-[10px] leading-relaxed text-[color:var(--wx-muted)]">
                Ringkasan mengikuti barang yang sudah didapat.{!omitFutureBillCaveat && " Tagihan dapat berubah jika ada barang atau ongkir berikutnya."}
            </p>
            <div className="mt-3 flex items-center justify-between gap-3 text-xs">
                <span data-testid="progress-text" className="text-[color:var(--wx-ink-2)] font-semibold">
                    {progress.label}
                </span>
                <span data-testid="progress-percent" className="shrink-0 text-[11px] font-bold text-[color:var(--wx-blue)]">{progress.percent === null ? "—" : `${progress.percent}%`}</span>
            </div>
            <div role="progressbar" aria-label="Pemrosesan barang" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress.percent ?? undefined} aria-valuetext={progress.label} className="mt-1.5 h-1.5 rounded-full bg-[color:var(--wx-surface)] overflow-hidden">
                <div
                    data-testid="progress-bar"
                    className="h-full bg-emerald-500 transition-all duration-500"
                    style={{ width: `${progress.percent ?? 0}%` }}
                />
            </div>
            <p data-testid="progress-status-counts" className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] leading-relaxed text-[color:var(--wx-muted)]">
                {PROGRESS_STATUSES.filter(status => status !== "RETUR" || progress.counts.RETUR > 0).map(status => (
                    <span key={status}>{status} {progress.valid ? `${progress.counts[status]} pcs` : "—"}{status === "RETUR" && " (perlu konfirmasi)"}</span>
                ))}
                {progress.counts.UNKNOWN > 0 && <span>Status belum jelas {progress.valid ? `${progress.counts.UNKNOWN} pcs` : "—"}</span>}
            </p>
            {updatedAt && (
                <p data-testid="order-updated-at" className="mt-2 text-[10px] text-[color:var(--wx-muted)]">
                    {refreshDelayed
                        ? `Pembaruan tertunda · Terakhir diperbarui ${updatedAt.toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}`
                        : `Diperbarui ${updatedAt.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}`}
                </p>
            )}
        </section>
    );
}
